"use client";

import * as React from "react";
import { ChevronDownIcon, DownloadIcon, PlusIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { ActionsMenu } from "./actions-menu";
import { Button } from "./button";
import { Tooltip } from "./tooltip";

/* -------------------------------------------------------------------------------------------------
 * Internal: shared by the described action rows (`HeaderPanelActionBar`, `ToolbarActions`).
 * Not exported from the package root — each bar publishes its own descriptor types.
 *
 * The descriptors are deliberately narrow. A descriptor carries what an action *is*, never how it
 * looks: there is no `variant`, `size` or `className`, so a caller cannot reach past the agreed
 * rules to restyle a control. Anything that must vary by record state is expressed as
 * `isVisible` / `disabled` and resolved by the caller before it reaches the bar.
 * -----------------------------------------------------------------------------------------------*/

type ActionDescriptorBase = {
	/** Sentence case, verb first, three words or fewer. */
	label: string;
	/** Renders greyed out rather than removed, so the row does not reflow between states. */
	disabled?: boolean;
	/** Tooltip explaining why the control is disabled. */
	disabledReason?: string;
	/** The only supported way to remove a control — reserved for missing permissions. */
	isVisible?: boolean;
	"data-testid"?: string;
	/** Overrides the accessible name when the visible label is not descriptive enough. */
	"aria-label"?: string;
};

type SelectHandler = () => void | Promise<unknown>;

/** An item inside a menu. Every menu item is icon-led. */
type DescribedMenuItem = ActionDescriptorBase & {
	/** Required: every menu item is icon-led. */
	icon: React.ReactNode;
	onSelect: SelectHandler;
};

/** A grey outline button in the row. */
type DescribedButtonAction = ActionDescriptorBase & {
	/** Required: side-by-side buttons need an icon to be told apart at a glance. */
	icon: React.ReactNode;
	onSelect: SelectHandler;
};

/**
 * One of the two stable menus.
 *
 * `download` collects exports. A single visible export is not worth a dropdown, so it renders as a
 * direct button with a download icon instead; two or more render as `Download ▾`.
 *
 * `add` collects "create a related thing" actions and keeps its dropdown even with one item, so the
 * affordance does not move around as a record gains and loses options.
 */
type DescribedMenuAction = ActionDescriptorBase & {
	type: "download" | "add";
	items: DescribedMenuItem[];
};

function isShown<T extends { isVisible?: boolean }>(item: T): boolean {
	return item.isVisible !== false;
}

/**
 * Pins the icon to the agreed size. `Button` only defaults an icon to 16px when it carries no size
 * class of its own, so setting one here is what keeps a caller's icon from being resized under us.
 */
function sizedIcon(icon: React.ReactNode, size: string): React.ReactNode {
	if (!React.isValidElement<{ className?: string }>(icon)) {
		return icon;
	}
	return React.cloneElement(icon, {
		className: cn(icon.props.className, size),
	});
}

/**
 * Tracks a pending `onSelect` so a control shows progress without the caller threading state
 * through. `ActionsMenu.Item` already spins while a returned promise settles; this keeps the row
 * buttons consistent with it, and means a descriptor never has to carry a loading flag.
 * Reporting a failure stays with the caller.
 */
function useAsyncSelect(onSelect: SelectHandler) {
	const [pending, setPending] = React.useState(false);
	const isMounted = React.useRef(true);

	React.useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	const run = React.useCallback(() => {
		const result = onSelect();
		if (!(result instanceof Promise)) {
			return;
		}
		setPending(true);
		const settle = () => {
			if (isMounted.current) {
				setPending(false);
			}
		};
		void result.then(settle, settle);
	}, [onSelect]);

	return { pending, run };
}

/** Wraps a disabled control so its tooltip still opens — a disabled button fires no pointer events. */
function withDisabledReason(
	control: React.ReactNode,
	{ disabled, disabledReason }: { disabled?: boolean; disabledReason?: string },
): React.ReactNode {
	if (!disabled || !disabledReason) {
		return control;
	}
	return (
		<Tooltip content={disabledReason} asChild>
			<span className="inline-flex" data-testid="disabled-reason">
				{control}
			</span>
		</Tooltip>
	);
}

function MenuItem({ item }: { item: DescribedMenuItem }) {
	return (
		<ActionsMenu.Item
			icon={sizedIcon(item.icon, "size-3.5")}
			onSelect={() => {
				const result = item.onSelect();
				if (result instanceof Promise) {
					return result.then(() => undefined);
				}
			}}
			disabled={item.disabled}
			disabledReason={item.disabledReason}
			aria-label={item["aria-label"]}
			data-testid={item["data-testid"]}
		>
			{item.label}
		</ActionsMenu.Item>
	);
}

function MenuItems({ items }: { items: DescribedMenuItem[] }) {
	return (
		<>
			{items.filter(isShown).map((item, index) => (
				<MenuItem key={`${item.label}-${index}`} item={item} />
			))}
		</>
	);
}

function RowButton({ action }: { action: DescribedButtonAction }) {
	const { pending, run } = useAsyncSelect(action.onSelect);

	return withDisabledReason(
		<Button
			variant="outline"
			size="sm"
			loading={pending}
			disabled={action.disabled}
			onClick={run}
			aria-label={action["aria-label"]}
			data-testid={action["data-testid"]}
		>
			{sizedIcon(action.icon, "size-3.5")}
			{action.label}
		</Button>,
		action,
	);
}

function RowMenu({ action }: { action: DescribedMenuAction }) {
	const visibleItems = action.items.filter(isShown);

	if (visibleItems.length === 0) {
		return null;
	}

	// A lone export reads as an action, not a menu.
	if (action.type === "download" && visibleItems.length === 1) {
		const [item] = visibleItems;
		return (
			<RowButton
				action={{
					label: item.label,
					icon: <DownloadIcon data-testid="download-icon" />,
					onSelect: item.onSelect,
					disabled: item.disabled || action.disabled,
					disabledReason: item.disabledReason ?? action.disabledReason,
					"aria-label": item["aria-label"],
					"data-testid": item["data-testid"] ?? action["data-testid"],
				}}
			/>
		);
	}

	return withDisabledReason(
		<ActionsMenu>
			<ActionsMenu.Trigger asChild>
				<Button
					variant="outline"
					size="sm"
					disabled={action.disabled}
					aria-label={action["aria-label"]}
					data-testid={action["data-testid"]}
				>
					{action.type === "add" ? <PlusIcon className="size-3.5" /> : null}
					{action.label}
					{action.type === "download" ? (
						<ChevronDownIcon className="size-3.5" />
					) : null}
				</Button>
			</ActionsMenu.Trigger>
			<ActionsMenu.Content>
				<MenuItems items={visibleItems} />
			</ActionsMenu.Content>
		</ActionsMenu>,
		action,
	);
}

export {
	isShown,
	MenuItem,
	MenuItems,
	RowButton,
	RowMenu,
	sizedIcon,
	useAsyncSelect,
	withDisabledReason,
};

export type {
	ActionDescriptorBase,
	DescribedButtonAction,
	DescribedMenuAction,
	DescribedMenuItem,
	SelectHandler,
};
